import { useCallback, useMemo, useState } from "react";
import type { Moment } from "moment";
import {
  calendarService,
  subscriptionService,
  taskService,
} from "@miplatform/api";
import { ATTEMPT_STATUSES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import {
  CalendarEventType,
  CalendarType,
  IApiRequest,
  IApiResponse,
  IApiResponseBase,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  IAttemptTrayConfig,
  IAttemptUpdateSubscriptionPayload,
  IAvailabilityLog,
  ICalendarConfig,
  ICalendarSet,
  IDateRangeISO,
  ITenantHolidayResponse,
  IUserListItemResponse,
} from "@miplatform/types";
import {
  addWorkDays,
  bugsnag,
  convertArrayToMap,
  createUUID,
  getDate,
  getDateISO,
  getGUIDArray,
  isSameOrAfter,
  isSameOrBefore,
} from "@miplatform/utils";
import { useGlobalManagerApproval } from "@mibuildweb/components";
import { loadCalendarEvents } from "./utils/loadCalendarEvents";
import { useUsers } from "../../hooks/useUser/useUsers";
import { useStoredCalendar, useStoredCalendarSet } from "../../stores";
import { FOLLOW_UP_TRAY_FILTERS } from "../../views/Config/Calendars/ConfigCalendarSetList.constants";
import { CalendarAttemptEvents } from "./CalendarProvider.types";

const loadEventsUUID = createUUID();
const SELECTED_USER_DAYS_RANGE = 5;

const getStartDate = ({
  calendarType,
  date,
  dateRange,
}: {
  calendarType: CalendarType | undefined;
  date: Moment;
  dateRange: IDateRangeISO;
}) => {
  if (calendarType === CalendarType.Kanban) {
    return getDate(dateRange.startDate);
  }

  return date;
};

const getEndDate = ({
  calendarType,
  date,
  dateRange,
  isUserSelected,
}: {
  calendarType: CalendarType | undefined;
  date: Moment;
  dateRange: IDateRangeISO;
  isUserSelected: boolean;
}) => {
  if (calendarType === CalendarType.Kanban) {
    return getDate(dateRange.endDate);
  }

  if (isUserSelected) {
    return addWorkDays(SELECTED_USER_DAYS_RANGE, date);
  }

  return undefined;
};

export const useCalendarProviderViewModel = ({
  calendarSetId = null,
  calendarId = null,
  selectedUser = null,
}: IUseCalendarProviderViewModelProps) => {
  const stateManager = useComponentState();
  const { executeApiRequest } = useGlobalManagerApproval();

  const { calendarSet } = useStoredCalendarSet(calendarSetId);

  const { calendarConfig } = useStoredCalendar(calendarSetId, calendarId);
  const { config: calendarSetConfig } = calendarSet ?? {};
  const { assignableUsers = [], schedulerUsers = [] } = calendarSetConfig ?? {};

  const activeCalendarId = calendarConfig?.id;

  const [users, setUsers] = useState<IUserListItemResponse[]>([]);
  const [events, setEvents] = useState<CalendarAttemptEvents>({});
  const [selectedAttempt, setSelectedAttempt] = useState<IAttempt | null>(null);
  const [zoom, setZoom] = useState(1);
  const [date, setDate] = useState<Moment>(getDate());
  const [tenantHolidays, setTenantHolidays] = useState<
    ITenantHolidayResponse[]
  >([]);
  const [dateRange, setDateRange] = useState<IDateRangeISO>({
    startDate: getDateISO(getDate()),
    endDate: getDateISO(getDate()),
  });
  const [trayBuckets, setTrayBuckets] = useState<IAttemptTrayConfig[]>([]);
  const [allTrayEvents, setAllTrayEvents] = useState<
    IAttemptListResponse[] | IAttemptListItemResponseForWorkOrder[]
  >([]);
  const [calendarCounts, setCalendarCounts] = useState<Record<string, number>>(
    {}
  );
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const activeTenantHolidays = useMemo(
    () =>
      tenantHolidays.filter(({ calendarSets }) =>
        calendarSets.some(({ calendarConfigs }) =>
          calendarConfigs.some(({ id }) => id === calendarConfig?.id)
        )
      ),

    [tenantHolidays, calendarConfig?.id]
  );

  const { getUsers } = useUsers();

  const [verticalTabActiveFilter, setVerticalTabActiveFilter] = useState<
    string | null
  >(null);

  const onDateRangeChange = (newDateRange: IDateRangeISO) => {
    setDateRange(newDateRange);
  };

  const getAvailabilityLogs: () => Promise<IAvailabilityLog[]> =
    useCallback(async () => {
      let filters;

      if (selectedUser) {
        filters = {
          userId: { eq: { value: selectedUser?.id, type: "guid" } },
          to: { gt: getDate(date).startOf("day").toDate() },
          from: {
            lt: addWorkDays(5, date).endOf("day").toDate(),
          },
        };
      } else {
        filters = {
          userId: { in: calendarConfig?.assignableUserIds },
          to: { gt: getDate(date).startOf("day").toDate() },
          from: { lt: getDate(date).endOf("day").toDate() },
        };
      }

      const { data, status } =
        await subscriptionService.users.getUserAvailabilityLogs({
          filters,
        });

      if (status !== HTTP_STATUSES.OK) return [];

      return data;
    }, [dateRange, date, calendarConfig?.assignableUserIds, selectedUser]);

  const loadUsers = useCallback(async () => {
    if (!calendarConfig) {
      return;
    }

    const { data, status } = await getUsers({
      filters: {
        id: {
          in:
            calendarConfig?.assignableUsers?.map(({ id }) => ({
              type: "guid",
              value: id,
            })) ?? [],
        },
      },
      sort: "firstName asc",
    });

    if (status === HTTP_STATUSES.OK) {
      const availabilityLog = await getAvailabilityLogs();
      data.forEach((user: IUserListItemResponse) => {
        user.availabilityLog = availabilityLog.filter(
          (log) => log.userId === user.id
        );
      });

      setUsers(data);
    }
  }, [calendarConfig, getAvailabilityLogs]);

  /**
   * TODO: Improve the speed of this getTrays call. Super slow. On average I am seeing 4+ seconds to load
   * const startTime = performance.now();
   * await getTrayDataForCalendar();
   * console.log(
   *  `Took ${(performance.now() - startTime).toFixed(2)}ms to get Trays`
   * )
   *
   * Unified tray data fetching - used by both Kanban buckets and HoldsList
   * @returns void
   */
  const getTrayDataForCalendar = async () => {
    if (
      !calendarSet?.config?.trays?.length ||
      !calendarSet?.config.calendarConfigs.length
    ) {
      setTrayBuckets([]);
      setAllTrayEvents([]);
      return;
    }

    const attachedTrayCalendarMap = Object.values(calendarSet.calendars).reduce(
      (acc: Record<string, string[]>, calendar) => {
        if (calendar.config.attachedTray) {
          if (!acc[calendar.config.attachedTray.id]) {
            acc[calendar.config.attachedTray.id] = [];
          }
          acc[calendar.config.attachedTray.id].push(calendar.config.id);
        }
        return acc;
      },
      {}
    );

    const orderTemplateIds =
      calendarSet.config.workTemplateBases?.map((template) => template.id) ??
      [];

    const trayBuckets: IAttemptTrayConfig[] = [];
    const allTrayData:
      | IAttemptListResponse[]
      | IAttemptListItemResponseForWorkOrder[] = [];
    const isWorkOrderEventType =
      calendarSet.config.eventType === CalendarEventType.WorkOrder;
    const assignmentPath = isWorkOrderEventType ? "workOrder" : "order";
    const templateIdPath = isWorkOrderEventType
      ? "workOrderTemplateId"
      : "orderTemplateId";
    const getAttempts = isWorkOrderEventType
      ? taskService.attempts.getAllWorkOrders
      : taskService.attempts.getAll;

    const trayPromises = calendarSet.config.trays.map(
      async (tray: IAttemptTrayConfig) => {
        const filterStringParts = [];
        if (tray?.filter) {
          filterStringParts.push(`(${tray?.filter})`);
        }

        if (orderTemplateIds.length) {
          filterStringParts.push(
            `assignment/${assignmentPath}/${templateIdPath} in (${orderTemplateIds.join(
              ","
            )})`
          );
        }

        // TODO : yuck, but elsewhere we are matching on filter to determine type like in the "Tray Type" dropdown in calendar configs
        // this will filter all Follow-Up trays to limit them to the attached Hold Reason Tabs/Calendar Ids, to prevent
        // trays that have no attached calendars (hold reasons) from finding orders from ALL calendars instead of none.
        if (tray?.filter === FOLLOW_UP_TRAY_FILTERS) {
          const trayAttachedCalendarIds =
            attachedTrayCalendarMap[tray.id] ?? [];
          if (trayAttachedCalendarIds.length < 1) {
            // a follow up tray with no attached calendars
            // this tray will always be empty, so skip the API call
            return {
              tray,
              data: [],
            };
          }
          filterStringParts.push(
            `assignment/${assignmentPath}/holdCalendarId in (${trayAttachedCalendarIds.join(
              ","
            )})`
          );
        }

        const filterString = filterStringParts.join(" and ");

        try {
          const response = await getAttempts({
            filters: filterString,
            ...(!isWorkOrderEventType && {
              params: {
                includeWorkOrders: true,
              },
            }),
            ...(tray.sort &&
              typeof tray.sort === "string" && { sort: tray.sort }),
          });

          if (response.status === HTTP_STATUSES.OK) {
            return {
              tray,
              data: response.data.map((attempt: IAttemptListResponse) => {
                return {
                  ...attempt,
                  isRequestedInDateRange:
                    attempt.assignment?.order?.requestedStartDate &&
                    isSameOrAfter(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.startDate
                    ) &&
                    isSameOrBefore(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.endDate ?? dateRange.startDate
                    ),
                };
              }),
            };
          }
        } catch (trayError) {
          bugsnag.setContext(
            "useCalendarProviderViewModel::getTrayDataForCalendar"
          );
          bugsnag.notify(
            trayError instanceof Error
              ? trayError
              : new Error(String(trayError))
          );
        }

        return {
          tray,
          data: [],
        };
      }
    );

    try {
      const trayResults = await Promise.all(trayPromises);
      trayResults.forEach(({ tray, data }) => {
        allTrayData.push(...data);

        trayBuckets.push({
          id: tray.id,
          isActive: tray.isActive,
          isDefault: tray.isDefault,
          name: tray.name,
          collapsible: tray.collapsible,
          displayOrder: tray.displayOrder,
          headerColor: tray.headerColor,
          filter: tray.filter,
          headerBackgroundColor: tray.headerBackgroundColor,
          contentBackgroundColor: tray.contentBackgroundColor,
          countBackgroundColor: tray.countBackgroundColor,
          countColor: tray.countColor,
          hideWhenEmpty: tray.hideWhenEmpty,
          data,
        });
      });

      trayBuckets.sort((a, b) => a.displayOrder - b.displayOrder);
    } catch (error) {
      bugsnag.setContext(
        "useCalendarProviderViewModel::getTrayDataForCalendar"
      );
      bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
    }

    setTrayBuckets(trayBuckets);
    setAllTrayEvents(allTrayData);
  };

  const resetTrays = () => {
    setAllTrayEvents([]);
    setTrayBuckets([]);
  };

  const handleLoadEvents = useCallback(async () => {
    stateManager.abort(loadEventsUUID);
    const startDate = getStartDate({
      calendarType: calendarConfig?.calendarType,
      date,
      dateRange,
    });
    const endDate = getEndDate({
      calendarType: calendarConfig?.calendarType,
      date,
      dateRange,
      isUserSelected: !!selectedUser,
    });

    return stateManager.run(async () => {
      setEvents({});
      if (calendarSetConfig && calendarConfig) {
        const { data: newEvents, status } = await loadCalendarEvents({
          calendarSetConfig,
          calendarConfig,
          startDate,
          endDate,
          userIds: selectedUser ? [selectedUser.id] : null,
          search,
        });

        if (status === HTTP_STATUSES.OK) {
          setEvents(convertArrayToMap(newEvents, "id"));
          getCalendarCounts(calendarSetConfig);
        }
      }
    }, loadEventsUUID);
  }, [
    calendarSetConfig,
    calendarConfig,
    date,
    dateRange,
    selectedUser,
    search,
  ]);

  const onAttemptsUpdate = useCallback(
    (payload: IAttemptUpdateSubscriptionPayload) => {
      setEvents((prev) => {
        const newEvents = { ...prev };
        for (const updatedEvent of payload) {
          const {
            attemptUpdatedEvent: { attemptId: updatedAttemptId },
          } = updatedEvent;

          const currentEvent = newEvents[updatedAttemptId];

          newEvents[updatedAttemptId] = {
            ...currentEvent,
            ...updatedEvent.attempt,
          };
        }

        return newEvents;
      });
    },
    [calendarSetConfig, calendarConfig?.id]
  );

  const unscheduleAttempt = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder) => {
      stateManager.run(async () => {
        const options = await taskService.attempts.unscheduleOptions({
          attemptId: attempt.id,
          modifiedOn: attempt.modifiedOn,
        });

        await executeApiRequest({
          options,
          toastMessageForManagerApproval: {
            text: "Order is locked. Manager approval needed.",
            subtext:
              "The order is locked and needs manager authorization before it can be moved.",
          },
          successToastMessageForManagerApproval: {
            text: "Attempt Unassigned",
          },
          callback: ({ status }: IApiResponseBase<unknown>) => {
            if (status === HTTP_STATUSES.OK) {
              handleLoadEvents();
            }
          },
        });
      });
    },
    [handleLoadEvents]
  );

  const holdWorkOrder = useCallback(
    (data: {
      attempt: IAttemptListItemResponseForWorkOrder;
      contactName: string;
      reason: string;
      description: string;
      followUpDate: string;
      calendarId: string;
    }) =>
      stateManager.run(async () =>
        taskService.workOrders.hold({
          id: data.attempt.assignment?.workOrder?.workOrderId,
          data: {
            id: data.attempt.assignment?.workOrder?.workOrderId,
            contactName: data.contactName,
            reason: data.reason,
            description: data.description,
            followUpDate: data.followUpDate,
            calendarId: data.calendarId,
            isOnHold: true,
          },
          modifiedOn: data.attempt.assignment?.workOrder?.modifiedOn,
          toastMessageConfig: {
            success: {
              text: `Work Order Hold Successful`,
              subtext: `Your work order hold was successful`,
            },
            error: {
              text: `Work Order Hold Failed`,
              subtext: `Your work order hold was not sent, please try again`,
            },
          },
        })
      ),
    [calendarSetConfig, activeCalendarId]
  );

  const clearWorkOrderSchedule = useCallback(
    async (user: IUserListItemResponse) => {
      const options = await taskService.attempts.clearWorkOrderScheduleOptions({
        data: {
          userId: user.id,
          date: date.toISOString(),
        },
      });
      await executeApiRequest({
        options,
        successToastMessageForManagerApproval: {
          text: `${user.name}'s schedule was cleared.`,
        },
        callback: ({ status }: IApiResponseBase<unknown>) => {
          if (status === HTTP_STATUSES.OK) {
            handleLoadEvents();
          }
        },
      });
    },
    [date, handleLoadEvents]
  );

  const setCalendarDate = (newDate: Date | string | Moment) => {
    setDate(getDate(newDate));
  };

  const handleCalendarSearch = useCallback((newValue: string) => {
    setSearch(newValue);
  }, []);

  const resetSearch = useCallback(() => {
    setSearch("");
  }, []);

  const getTenantHolidays = async () => {
    const response = await calendarService.tenantHolidays.getAll();
    if (response.status === HTTP_STATUSES.OK) {
      setTenantHolidays(response.data || []);
    }
  };

  const getCalendarCount = async (
    calendar: ICalendarConfig,
    getter: (params: IApiRequest) => Promise<IApiResponse<IAttempt[]>>
  ) => {
    const response = await getter({
      filters: {
        ...calendar.filter,
        attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
        isOrderFollowupDue: false,
        assignment: {
          ...(calendarSetConfig && {
            [calendarSetConfig?.eventType]: {
              [`${calendarSetConfig?.eventType}TemplateId`]: {
                in: getGUIDArray(
                  (calendar.workTemplateBases || []).map(({ id }) => id)
                ),
              },
              holdCalendarId: {
                type: "guid",
                value: calendar.id,
              },
            },
          }),
        },
      },
      sort: "modifiedOn desc",
      top: 0,
    });
    if (response.status === HTTP_STATUSES.OK) {
      return (response.headers.totalcount as number) || 0;
    }

    return 0;
  };

  const getCalendarCounts = (calendarSet: ICalendarSet) => {
    if (!calendarSet) return;

    const getter =
      calendarSet.eventType === CalendarEventType.WorkOrder
        ? taskService.attempts.getAllWorkOrders
        : taskService.attempts.getAll;
    stateManager.run(async () => {
      const counts = await Promise.all(
        calendarSet?.calendarConfigs
          .filter(
            (calendar: ICalendarConfig) =>
              calendar.calendarType === CalendarType.HoldsList
          )
          .map(async (calendar) => {
            const count = await getCalendarCount(calendar, getter);

            return {
              id: calendar.id,
              count,
            };
          })
      );

      setCalendarCounts(
        counts.reduce((acc, { id, count }) => {
          if (count !== null) {
            acc[id] = count;
          }
          return acc;
        }, {} as Record<string, number>)
      );
    });
  };

  return {
    calendarSet,
    calendarConfig,
    events,
    assignableUsers,
    schedulerUsers,
    activeCalendarId,
    zoom,
    isLoading: stateManager.isBusy(),
    date,
    dateRange,
    search,
    debouncedSearch,
    trayBuckets,
    allTrayEvents,
    selectedAttempt,
    calendarCounts,
    verticalTabActiveFilter,
    users,
    tenantHolidays: activeTenantHolidays,
    loadUsers,
    holdWorkOrder,
    handleCalendarSearch,
    setCalendarDate,
    setZoom,
    onAttemptsUpdate,
    unscheduleAttempt,
    handleLoadEvents,
    onDateRangeChange,
    getTrayDataForCalendar,
    getTenantHolidays,
    resetTrays,
    resetSearch,
    clearWorkOrderSchedule,
    setSelectedAttempt,
    setVerticalTabActiveFilter,
  };
};

interface IUseCalendarProviderViewModelProps {
  calendarSetId?: string | null;
  calendarId?: string | null;
  selectedUser?: IUserListItemResponse | null;
}
